import type { ProjectsKanbanData, StageKey } from './types';

export interface QuoteOperationsSummary {
  activeCount: number;
  totalCount: number;
  totalValue: number;
  byStage: Partial<Record<StageKey, number>>;
}

export function summarizeQuoteKanban(data?: ProjectsKanbanData): QuoteOperationsSummary {
  const byStage: Partial<Record<StageKey, number>> = {};
  let totalValue = 0;
  let activeCount = 0;

  for (const column of data?.columns ?? []) {
    const count = column.totals.projectCount;
    if (count > 0) {
      byStage[column.stage] = count;
    }
    totalValue += column.totals.value;
    if (column.stage !== 'done') {
      activeCount += count;
    }
  }

  return {
    activeCount,
    totalCount: data?.totalCount ?? 0,
    totalValue,
    byStage,
  };
}

export interface QuoteStatusSummaryEntry {
  status: string;
  count: number;
  amount: number;
}

/**
 * Operations Kanban summary tiles group the fulfillment STAGES into three
 * buckets so the tiles reconcile with the columns (a commercial-status grouping
 * dropped in-between statuses like `confirmed` / `invoice-ready`). Must mirror
 * the server-side OPERATIONS_STAGE_BUCKETS in dao/quotes-v4.js.
 */
export const OPERATIONS_STAGE_BUCKETS: { key: string; stages: StageKey[] }[] = [
  { key: 'approved', stages: ['new_purchase_order'] },
  {
    key: 'inprogress',
    stages: [
      'planification',
      'picking_packing',
      'platform_configuration',
      'ready_to_ship',
      'shipping',
      'installation_calibration',
      'invoicing',
    ],
  },
  { key: 'invoiced', stages: ['customer_onboarding', 'done'] },
];

/**
 * Client fallback for the Kanban summary bar when the server omits
 * `statusSummary`: buckets the columns by stage (Approved / In Progress /
 * Invoiced) using the same column totals the server sums, keyed the same way.
 */
export function computeStageBucketSummary(data?: ProjectsKanbanData): QuoteStatusSummaryEntry[] {
  const stageToBucket = new Map<string, string>();
  OPERATIONS_STAGE_BUCKETS.forEach((b) => b.stages.forEach((s) => stageToBucket.set(s, b.key)));

  const totals = new Map<string, { count: number; amount: number }>(
    OPERATIONS_STAGE_BUCKETS.map((b) => [b.key, { count: 0, amount: 0 }]),
  );

  for (const column of data?.columns ?? []) {
    const bucketKey = stageToBucket.get(column.stage);
    if (!bucketKey) continue;
    const entry = totals.get(bucketKey);
    if (!entry) continue;
    entry.count += column.totals.projectCount;
    entry.amount += column.totals.value;
  }

  return OPERATIONS_STAGE_BUCKETS.map((b) => {
    const entry = totals.get(b.key) ?? { count: 0, amount: 0 };
    return { status: b.key, count: entry.count, amount: entry.amount };
  });
}
